'use client'

import { useRouter, useSearchParams } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Lightbulb,
  Code2,
  GraduationCap,
  CheckCircle2,
  Calendar
} from 'lucide-react'

import { FadeUp } from '@/components/ui/fade-up'
const services = {
  consulting: {
    title: 'DXコンサルティング',
    description: '地域の課題を深く理解し、最適なDX戦略を策定',
    items: [
      {
        id: 'tech-advisory',
        title: 'CTO as a Service (技術アドバイザリー)',
        description: '最新技術の活用方法から、既存システムとの連携まで。技術面での意思決定を支援します。',
        features: [
          '技術選定支援',
          'アーキテクチャ設計',
          'セキュリティ評価',
          'ベンダー選定支援'
        ],
        duration: '継続契約',
        price: '¥5万円/月〜'
      }
    ]
  },
  development: {
    title: '開発・実装',
    description: '地域に最適化されたシステムを開発・実装',
    items: [
      {
        id: 'poc-development',
        title: 'PoC開発',
        description: '本格導入前の実証実験。小規模から始めて効果を検証し、リスクを最小化します。',
        features: [
          'プロトタイプ開発',
          '効果測定・分析',
          'フィードバック収集',
          '本格導入計画'
        ],
        duration: '1-2ヶ月',
        price: '¥100万円〜'
      }
    ]
  },
  education: {
    title: '教育・研修',
    description: '地域のDX人材を育成する実践的プログラム',
    items: [
      {
        id: 'shukko-training',
        title: '出向教育',
        description: 'Digital Pride Hiroshimaで実務を通じてDXスキルを習得。企業からの出向者を受け入れ、実践的なプロジェクトに参加しながら学習します。',
        features: [
          '実案件での実践学習',
          'AI駆動開発スキル習得',
          '地域DX実務経験',
          '定期的なフィードバック'
        ],
        duration: '半年〜1年間',
        price: '個別相談'
      },
      {
        id: 'leader-training',
        title: 'DXリーダー育成',
        description: '組織のDXを推進するリーダーを育成。戦略立案から実行まで包括的にカバー。',
        features: [
          'リーダーシップ開発',
          'プロジェクト管理',
          'チェンジマネジメント',
          'メンタリング'
        ],
        duration: '3ヶ月',
        price: '¥100万円〜'
      }
    ]
  }
}

export function ServicesContent() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const tab = searchParams.get('tab') || 'education'

  const handleTabChange = (value: string) => {
    router.push(`/services?tab=${value}`)
  }

  return (
    <>
      {/* ヒーローセクション */}
      <section className="relative bg-[#BEB0A8] py-12 md:py-16">
        <div
          className="animate-fade-up container"
        >
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-4 font-['Noto_Sans_JP',_sans-serif] bg-[#292623] text-white">地域DXを支える包括的サービス</Badge>
            <h1 className="font-['Montserrat',_sans-serif] text-[32px] md:text-[46px] font-semibold text-[#292623] tracking-[-0.92px] mb-6">
              サービス一覧
            </h1>
            <p className="font-['Noto_Sans_JP',_sans-serif] text-[16px] md:text-[18px] text-[#292623]/80">
              戦略策定から開発・導入・運用まで。<br />
              地域DXの全ての段階で、最適なサービスを提供します。
            </p>
          </div>
        </div>
      </section>

      {/* サービスカテゴリー */}
      <section className="py-16">
        <div className="container">
          <Tabs value={tab} onValueChange={handleTabChange} className="space-y-8">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-3 h-auto!">
              <TabsTrigger value="education" className="flex h-auto flex-col gap-2 py-4 font-['Noto_Sans_JP',_sans-serif] data-[state=active]:bg-[#292623] data-[state=active]:text-white">
                <GraduationCap className="h-6 w-6" />
                <span>教育・研修</span>
              </TabsTrigger>
              <TabsTrigger value="consulting" className="flex h-auto flex-col gap-2 py-4 font-['Noto_Sans_JP',_sans-serif] data-[state=active]:bg-[#292623] data-[state=active]:text-white">
                <Lightbulb className="h-6 w-6" />
                <span>コンサルティング</span>
              </TabsTrigger>
              <TabsTrigger value="development" className="flex h-auto flex-col gap-2 py-4 font-['Noto_Sans_JP',_sans-serif] data-[state=active]:bg-[#292623] data-[state=active]:text-white">
                <Code2 className="h-6 w-6" />
                <span>開発・実装</span>
              </TabsTrigger>
            </TabsList>

            {Object.entries(services).map(([key, category]) => (
              <TabsContent key={key} value={key} className="space-y-8">
                <div className="text-center">
                  <h2 className="text-3xl font-bold mb-4">{category.title}</h2>
                  <p className="text-lg text-muted-foreground">{category.description}</p>
                </div>

                <div className="grid gap-8">
                  {category.items.map((service, index) => (
                    <FadeUp delay={index * 0.1}
                      key={service.id}
                    >
                      <Card>
                        <CardHeader>
                          <div className="flex items-start justify-between">
                            <div>
                              <CardTitle className="text-2xl">{service.title}</CardTitle>
                              <CardDescription className="mt-2">
                                {service.description}
                              </CardDescription>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="grid md:grid-cols-2 gap-6">
                            <div>
                              <h4 className="font-semibold mb-3">提供内容</h4>
                              <ul className="space-y-2">
                                {service.features.map((feature, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <CheckCircle2 className="h-5 w-5 text-[#292623] mr-2 mt-0.5" />
                                    <span className="text-sm">{feature}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div className="space-y-4">
                              <div className="flex items-center text-sm">
                                <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                                <span className="text-muted-foreground">期間:</span>
                                <span className="ml-2 font-medium">{service.duration}</span>
                              </div>

                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </FadeUp>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* プロセス */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <FadeUp
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold mb-4">導入までの流れ</h2>
            <p className="text-lg text-muted-foreground">
              お問い合わせから導入まで、専門チームが一貫してサポート
            </p>
          </FadeUp>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'お問い合わせ',
                description: 'フォームからお気軽にご相談ください'
              },
              {
                step: '02',
                title: 'ヒアリング',
                description: '1時間無料相談で課題や要望を詳しくお伺いします'
              },
              {
                step: '03',
                title: 'ご提案',
                description: '課題解決に向けた具体的なプランをご提示'
              }
            ].map((process, index) => (
              <FadeUp delay={index * 0.1}
                key={index}
                className="text-center"
              >
                <div className="w-16 h-16 bg-[#292623] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {process.step}
                </div>
                <h3 className="font-semibold mb-2">{process.title}</h3>
                <p className="text-sm text-muted-foreground">{process.description}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container">
          <FadeUp
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-3xl font-bold mb-4">
              DXの悩み、お聞かせください
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              あなたの地域や企業の「らしさ」を、成長力に変えるDXを。<br />
              まずは1時間、お話を聞かせてください。
            </p>
            <div className="flex justify-center">
              <Button size="lg" className="bg-[#292623] text-white hover:bg-[#292623]/90 font-['Noto_Sans_JP',_sans-serif]" asChild>
                <a href="/contact?type=consultation">
                  無料相談を申し込む
                </a>
              </Button>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  )
}